import React, { forwardRef, useCallback, useId, useState } from 'react';
import { t } from '@lingui/macro';
import moment from 'moment';

import { AvatarType, getAvatarImage, Size } from '@rover/components/src/Avatar';
import { schema } from '@rover/components/src/microdata';
import FlatButton from '@rover/components/src/OverflowTextModal/FlatButton';
import StackedAvatar from '@rover/components/src/StackedAvatar';
import { DEFAULT_LANG } from '@rover/constants/src/i18n/language';
import CalendarCancellation from '@rover/icons/svg/calendar-cancellation.svg';
import { Box, Flex, Paragraph, Text } from '@rover/kibble/core';
import IconBlock from '@rover/kibble/patterns/IconBlock';
import StarRating from '@rover/kibble/patterns/StarRating/StarRating';
import A11yHiddenBox from '@rover/kibble/styles/A11yHiddenBox';
import useTheme from '@rover/kibble/themes/useTheme';
import { TrainerProfileReviewClicked } from '@rover/react-lib/src/pages/training/events/analyticsEvents';
import { ReviewDispatch } from '@rover/rsdk/src/apiClient/latest/model/reviewDispatch';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import {
  FLAGS,
  useFrontendConfiguration,
} from '@rover/rsdk/src/modules/Data/Core/FrontendConfiguration';
import { Logger } from '@rover/rsdk/src/modules/Logging';
import getDateTimeFormatMapForLang from '@rover/utilities/datetime';
import emitMetric from '@rover/utilities/emitMetric';

import {
  getIcon,
  isAnyRating,
  isAnyTestimonial,
  isAutomaticRecord,
  isExternalReview,
} from './utils';

export type Props = {
  review: ReviewDispatch;
  onReadMore?: () => void;
  onLearnMore?: () => void;
  full?: boolean;
  variant?: 'training';
  showRating?: boolean;
  compact?: boolean;
};

const DF = getDateTimeFormatMapForLang(DEFAULT_LANG);

const MAX_NUM_LINES = 4;
const LINE_HEIGHT = 24;

const Review = forwardRef<HTMLElement, Props>(
  (
    {
      review,
      onReadMore,
      onLearnMore,
      full = false,
      variant,
      showRating = false,
      compact = false,
    }: Props,
    ref
  ) => {
    const theme = useTheme();
    const { isFlagActive } = useFrontendConfiguration();
    const enableRichResults = isFlagActive(FLAGS.ROLLOUT_SITTER_PROFILE_RICH_RESULTS);
    const [isTruncated, setIsTruncated] = useState<boolean>(false);

    const handleReadMore = (): void => {
      onReadMore && onReadMore();
      if (variant === 'training') {
        const analyticsEvent = new TrainerProfileReviewClicked();
        emitAnalyticsEvent(analyticsEvent);
        Logger.info(analyticsEvent.eventName);
      } else {
        emitMetric('sitter_profile_review_clicked');
        Logger.info('sitter_profile_review_clicked');
      }
    };

    const service = (isAnyRating(review) || isAutomaticRecord(review)) && review.service;

    const petParentName =
      (isAnyTestimonial(review) || isAnyRating(review)) && review.poster.shortName;

    const reviewText = review.description;
    const petAvatars =
      isAnyRating(review) &&
      review.pets?.map((pet) =>
        getAvatarImage(pet.defaultImage.bigThumb, pet.petType as AvatarType)
      );

    // Use the '.utc()' method to remove the timezone difference to avoid
    // render differences between the server (usually GMT) and the client (any timezone)
    const reviewDate = moment(review.added).utc().format(DF.DATE_MED);
    const subtitle = `${service ? `${service.name} • ` : ''}  ${reviewDate}`;

    const paragraphStyle = {
      display: '-webkit-box',
      overflow: 'hidden',
      'white-space': 'pre-line',
      '-webkit-line-clamp': `${MAX_NUM_LINES}`,
      '-webkit-box-orient': 'vertical',
    };

    const reviewDescriptionId = useId();
    const measurementRef = useCallback((node) => {
      if (node) {
        const { height } = node.getBoundingClientRect();
        if (Math.round(height / LINE_HEIGHT) > MAX_NUM_LINES) {
          setIsTruncated(true);
        }
      }
    }, []);

    return (
      <>
        <Box webProps={{ tabIndex: -1 }} /> {/* To focus on this component using JS */}
        <Flex
          flexDirection="column"
          width="100%"
          webProps={
            enableRichResults
              ? {
                  itemScope: true,
                  itemType: schema`Review`,
                  itemProp: 'review',
                }
              : undefined
          }
        >
          {enableRichResults && (
            <A11yHiddenBox>
              {/* TODO: Use structured author markup on visible Text when Kibble Text supports microdata props */}
              <span itemScope itemType={schema`Person`} itemProp="author">
                <meta itemProp="name" content={petParentName || ''} />
              </span>
              <meta itemProp="datePublished" content={review.added} />
              {isAnyRating(review) && (
                <span itemScope itemType={schema`Rating`} itemProp="reviewRating">
                  <meta itemProp="bestRating" content="5" />
                  <meta itemProp="ratingValue" content={String(review.overall)} />
                  <meta itemProp="worstRating" content="0" />
                </span>
              )}
            </A11yHiddenBox>
          )}
          <Flex
            width="100%"
            flexDirection="row"
            justifyContent="space-between"
            alignItems="center"
            pb={isExternalReview(review) ? '0x' : '4x'}
          >
            <Box id={reviewDescriptionId} flexDirection="column" ref={ref}>
              {petParentName && <Text fontWeight="semibold">{petParentName}</Text>}
              {showRating && isAnyRating(review) && (
                <Box pt="1x">
                  <StarRating rating={review.overall} variant="expand" textSize="100" />
                </Box>
              )}
              {(isAnyRating(review) || isAutomaticRecord(review)) && service && (
                <Box pt="1x" sx={{ 'white-space': 'nowrap' }}>
                  <IconBlock
                    iconSize="16px"
                    size="100"
                    fontWeight="regular"
                    title={subtitle}
                    icon={getIcon(service.icon)}
                    iconMargin="2x"
                  />
                </Box>
              )}
              {isAnyTestimonial(review) && (
                <Box pt="1x">
                  <Text size="100" fontWeight="regular">
                    {subtitle}
                  </Text>
                </Box>
              )}
            </Box>
            {!compact && isAnyRating(review) && petAvatars && (
              <StackedAvatar imageUrls={petAvatars} size={Size.Small} />
            )}
            {isAutomaticRecord(review) && (
              <Box
                height="48px"
                width="48px"
                borderRadius="40px"
                justifyContent="center"
                alignItems="center"
                sx={{ backgroundColor: theme.background.tertiary }}
              >
                <CalendarCancellation
                  style={{ position: 'relative', top: '16px', left: '16px' }}
                  height={16}
                  width={16}
                />
              </Box>
            )}
          </Flex>
          <Box ref={measurementRef}>
            <Paragraph
              mb="0x"
              sx={!full && isTruncated ? paragraphStyle : {}}
              webProps={enableRichResults ? { itemProp: 'description' } : undefined}
            >
              {reviewText}
            </Paragraph>
          </Box>
          {compact
            ? isAnyRating(review) &&
              petAvatars && <StackedAvatar imageUrls={petAvatars} size={Size.ExtraSmall} />
            : isTruncated &&
              !full && (
                <Box pt="4x">
                  <FlatButton aria-describedby={reviewDescriptionId} onClick={handleReadMore}>
                    {t`Read more`}
                  </FlatButton>
                </Box>
              )}
          {isAutomaticRecord(review) && (
            <Box pt="4x">
              <FlatButton aria-describedby={reviewDescriptionId} onClick={onLearnMore}>
                {t`Learn more`}
              </FlatButton>
            </Box>
          )}
        </Flex>
      </>
    );
  }
);

export default Review;
